import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import DashboardOverview from "@/pages/dashboard/DashboardOverview";
import DocumentsPage from "@/pages/dashboard/DocumentsPage";
import MessagesPage from "@/pages/dashboard/MessagesPage";
import DebtsPage from "@/pages/dashboard/DebtsPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import StartCasePage from "@/pages/dashboard/StartCasePage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeadsPage from "@/pages/admin/AdminLeadsPage";
import AdminCasesPage from "@/pages/admin/AdminCasesPage";
import AdminDocumentsPage from "@/pages/admin/AdminDocumentsPage";
import AdminMessagesPage from "@/pages/admin/AdminMessagesPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";

// Protected Route wrapper for client users
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-10 h-10 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected Route wrapper for admin
const AdminProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("admin_token");
  if (!token) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      
      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardOverview />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/debts" element={
        <ProtectedRoute>
          <DebtsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/start-case" element={
        <ProtectedRoute>
          <StartCasePage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={
        <AdminProtectedRoute>
          <AdminDashboard />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <AdminProtectedRoute>
          <AdminLeadsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/cases" element={
        <AdminProtectedRoute>
          <AdminCasesPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/documents" element={
        <AdminProtectedRoute>
          <AdminDocumentsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <AdminProtectedRoute>
          <AdminMessagesPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <AdminProtectedRoute>
          <AdminAnalyticsPage />
        </AdminProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
